import * as React from "react";

/** What a closing Base UI menu focuses on its way out: the clicked item, the
 *  popup, or the trigger it restores focus toward. */
const MENU_MACHINERY_SELECTOR =
  "[role=menu],[role=menuitem],[aria-haspopup],[data-slot=menu-trigger],[data-slot=context-menu-trigger]";

/** How long after editing begins a blur into menu machinery still reads as
 *  the launching menu's own bookkeeping rather than a user gesture. */
const STEAL_WINDOW_MS = 500;

/**
 * Guards an editor that a MENU opens (a row/card menu's Rename) against the
 * menu's own closing focus traffic. As a Base UI menu finishes closing it
 * focuses the clicked item and then restores focus toward its trigger — both
 * landing AFTER the editor's autoFocus, so the resulting blur would
 * commit-and-dismiss an editor the user just opened.
 *
 * Pass whether externally-launched editing is active; call the returned
 * function first in the editor's `onBlur`. It answers true — after reclaiming
 * focus — when the blur is that bookkeeping: focus went to menu machinery
 * within the steal window right after editing began. Both conditions are
 * required: the target test alone would eat a real click back on the trigger,
 * and the window alone would eat any fast dismissal.
 *
 * The window anchor is stamped during RENDER on the rest→editing transition,
 * never in an effect — the steal is a same-tick blur that arrives before any
 * passive effect runs.
 */
export function useMenuFocusStealGuard(
  editing: boolean,
): (event: React.FocusEvent<HTMLElement>) => boolean {
  const editingSinceRef = React.useRef(0);
  const wasEditingRef = React.useRef(false);
  if (editing && !wasEditingRef.current) {
    editingSinceRef.current = performance.now();
  }
  wasEditingRef.current = editing;

  return React.useCallback(
    (event: React.FocusEvent<HTMLElement>): boolean => {
      if (!wasEditingRef.current) return false;
      const stolenByMenu =
        event.relatedTarget instanceof HTMLElement &&
        event.relatedTarget.closest(MENU_MACHINERY_SELECTOR) !== null;
      if (
        stolenByMenu &&
        performance.now() - editingSinceRef.current < STEAL_WINDOW_MS
      ) {
        event.target.focus();
        return true;
      }
      return false;
    },
    [],
  );
}
